import React from 'react';
import { 
  Typography, 
  Grid, 
  Box,
  Card,
  CardContent,
  Stack,
  Divider
} from '@mui/material';
import PageTemplate from '../components/PageTemplate';
import {
  Security as SecurityIcon,
  Timer as TimerIcon,
  AppRegistration as AppRegistrationIcon,
  Public as PublicIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  Devices as DevicesIcon,
  Storage as StorageIcon
} from '@mui/icons-material';

// Feature item component
const FeatureItem = ({ 
  icon, 
  title, 
  description 
}: { 
  icon: React.ReactNode; 
  title: string; 
  description: string; 
}) => (
  <Card 
    elevation={0}
    sx={{ 
      height: '100%',
      borderRadius: 3,
      transition: 'transform 0.2s, box-shadow 0.2s',
      border: '1px solid',
      borderColor: 'divider',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
      }
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box 
          sx={{ 
            color: 'primary.main',
            width: 50,
            height: 50,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: theme => theme.palette.mode === 'light' 
              ? 'rgba(0, 0, 0, 0.05)' 
              : 'rgba(255, 255, 255, 0.08)',
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Stack>
    </CardContent>
  </Card>
);

const FeaturesPage: React.FC = () => {
  const features = [
    {
      icon: <SecurityIcon fontSize="large" />,
      title: 'Secure & Anonymous',
      description: 'Your privacy is our priority. Our temporary email service ensures total anonymity with end-to-end encryption, keeping your personal information protected.'
    },
    {
      icon: <TimerIcon fontSize="large" />,
      title: 'Temporary',
      description: 'Create disposable email addresses that automatically expire after a set period, minimizing the risk of spam and data breaches.'
    },
    {
      icon: <AppRegistrationIcon fontSize="large" />,
      title: 'No Registration',
      description: "Start using 1TimeMail instantly without any signup or personal information. Simply generate an address and you're ready to go."
    },
    {
      icon: <PublicIcon fontSize="large" />,
      title: 'Private Domain',
      description: 'We use our own private domain for temporary emails, ensuring better deliverability and reducing the chances of being blacklisted.'
    },
    {
      icon: <SpeedIcon fontSize="large" />,
      title: 'Instant Access',
      description: 'Emails are delivered instantly to your temporary inbox. No waiting, no refreshing – just immediate access to your messages.'
    },
    {
      icon: <ShieldIcon fontSize="large" />,
      title: 'Spam Protection',
      description: 'Our advanced filters block unwanted spam, keeping your temporary inbox clean and focused on the messages that matter.'
    },
    {
      icon: <DevicesIcon fontSize="large" />,
      title: 'Mobile Friendly',
      description: 'Access your temporary email from any device with our responsive design that works seamlessly on desktop, tablet, and mobile.'
    },
    {
      icon: <StorageIcon fontSize="large" />,
      title: 'File Attachments',
      description: 'Receive and view file attachments directly in your temporary inbox. Download them or preview compatible file types in-browser.'
    }
  ];

  return (
    <PageTemplate 
      title="Features" 
      description="Discover why 1TimeMail is the best choice for temporary email needs with our comprehensive set of privacy-focused features."
      breadcrumbs={[{ label: 'Features', path: '/features' }]}
    >
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          What Makes 1TimeMail Different
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 800 }}>
          We've designed 1TimeMail to be the most secure, user-friendly, and reliable temporary email service available. Here's why thousands of users choose us every day:
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <FeatureItem 
              icon={feature.icon} 
              title={feature.title} 
              description={feature.description} 
            />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 6 }} />

      <Box>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Committed to Your Privacy
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          At 1TimeMail, we believe privacy is a fundamental right. That's why we've built our service with privacy at its core:
        </Typography>
        <ul>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            No logs of email content
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            Automatic deletion after expiry
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            No tracking or profiling
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary">
            Zero ads or third-party analytics
          </Typography>
        </ul>
      </Box>
    </PageTemplate>
  );
};

export default FeaturesPage; 